import type { CompletedChecks, QueueRow } from '@myrewards/contracts';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useRef, useState, type ReactElement } from 'react';
import { completeChecks } from '../../api/ops';
import { Button, ConfirmDialog, formatCount, formatMoney, plural, useToast } from '../../ui';
import { amountWords, CHECK_LEFT, problemWords } from '../labels';
import { TickBox } from '../shared';
import s from '../Ops.module.css';

/**
 * What is ticked (APP-135 §5): the awards ticked on the page being shown, or, after "Select all N
 * ready, on every page", those N awards as they were when it was pressed. An award that becomes
 * ready later isn't in it until someone asks again: nobody marks an award they haven't seen without
 * asking to. Changing page clears the first, not the second.
 */
export type QueueSelection =
  { mode: 'page'; ids: ReadonlySet<string> } | { mode: 'all'; ids: ReadonlySet<string> };

export const NO_SELECTION: QueueSelection = { mode: 'page', ids: new Set() };

/** The rows at the check that can be marked checked from the list (APP-105). */
export function readyRows(rows: readonly QueueRow[]): QueueRow[] {
  return rows.filter((r) => r.check?.ready === true);
}

/** What a selection adds up to: awards, people, cash and options, in words. */
export function selectionWords(rows: readonly QueueRow[]): string {
  const people = rows.reduce((n, r) => n + r.people, 0);
  const cash = rows.reduce((n, r) => n + r.cashCents, 0);
  const options = rows.reduce((n, r) => n + (r.options ?? 0), 0);
  return [
    plural(rows.length, 'award', 'awards'),
    plural(people, 'person', 'people'),
    `${formatMoney(cash)} cash`,
    ...(options > 0 ? [`${formatCount(options)} options`] : []),
  ].join(' · ');
}

/** How many awards the confirm names one by one, with their people (APP-109). */
const NAMED = 6;

/** The awards a toast or a confirm names: the first few, then how many more. */
function awardList(ids: readonly string[]): string {
  const shown = ids.slice(0, NAMED).join(', ');
  return ids.length > NAMED ? `${shown} and ${String(ids.length - NAMED)} more` : shown;
}

/** "Ben Adeyemi", "Ben Adeyemi and Lucia Ferraro", "Ben Adeyemi, Lucia Ferraro, Chris Nguyen and 2 more". */
export function peopleWords(row: QueueRow): string {
  const names = (row.check?.people ?? []).map((p) => p.person.name);
  if (names.length === 0) return plural(row.people, 'person', 'people');
  const shown = names.slice(0, 3);
  const more = names.length - shown.length;
  if (more > 0) return `${shown.join(', ')} and ${formatCount(more)} more`;
  const last = shown.pop() ?? '';
  return shown.length === 0 ? last : `${shown.join(', ')} and ${last}`;
}

/**
 * The confirm's summary (APP-109): each award with who it pays and how much, the first six by
 * name, then the rest by number, and what they add up to.
 */
export function confirmRows(rows: readonly QueueRow[]): { label: string; value: string }[] {
  const named = rows.slice(0, NAMED).map((r) => ({
    label: r.award.displayId,
    value: `${peopleWords(r)} · ${amountWords(r.cashCents, r.options ?? 0)}`,
  }));
  const rest = rows.slice(NAMED);
  return [
    ...named,
    ...(rest.length === 0
      ? []
      : [
          {
            label: `And ${plural(rest.length, 'more award', 'more awards')}`,
            value: awardList(rest.map((r) => r.award.displayId)),
          },
        ]),
    { label: 'Adds up to', value: selectionWords(rows) },
  ];
}

/** What Mark checked did, as one toast: how many went to letters, which stayed and why. */
export function resultToast(result: CompletedChecks): {
  tone: 'done' | 'problem';
  title: string;
  body: string;
} {
  const done = result.checked.length;
  const stayed = result.left.map(
    (l) => `${l.award.displayId === '' ? 'An award' : l.award.displayId}: ${CHECK_LEFT[l.reason]}`,
  );
  if (stayed.length === 0)
    return {
      tone: 'done',
      title: `${plural(done, 'award', 'awards')} marked checked`,
      body: 'They’re with you for their letters.',
    };
  return {
    tone: done === 0 ? 'problem' : 'done',
    title:
      done === 0
        ? 'Nothing was marked checked'
        : `${plural(done, 'award', 'awards')} marked checked · ${formatCount(stayed.length)} stayed in the queue`,
    body: `Stayed: ${stayed.slice(0, 4).join('; ')}${stayed.length > 4 ? `; and ${String(stayed.length - 4)} more` : ''}.`,
  };
}

/** The box beside an award's number: tick it to mark it checked with the others (APP-105). */
export function SelectAward({
  row,
  selected,
  onChange,
}: {
  row: QueueRow;
  selected: boolean;
  onChange: (on: boolean) => void;
}): ReactElement {
  return (
    <TickBox isSelected={selected} onChange={onChange} label={`Select ${row.award.displayId}`}>
      {null}
    </TickBox>
  );
}

/**
 * Above the queue (APP-105): Select all ready on this page, what the selection adds up to, and
 * Mark N checked. Ticks act on the page you see; "Select all N ready, on every page" reaches the
 * rest (APP-135). One confirm for all of them; each award then goes through on its own, and any
 * that changed or that a teammate took stays in the queue with the reason.
 */
export function MarkCheckedBar({
  rows,
  pageRows,
  selection,
  onSelect,
  details,
}: {
  /** Every row in the view, on every page. */
  rows: readonly QueueRow[];
  /** The rows on the page being shown. */
  pageRows: readonly QueueRow[];
  selection: QueueSelection;
  onSelect: (next: QueueSelection) => void;
  /** APP-109: open or close the shown page's award details at once. */
  details: { allOpen: boolean; onAllOpen: (open: boolean) => void };
}): ReactElement {
  const toast = useToast();
  const queryClient = useQueryClient();
  const [confirming, setConfirming] = useState(false);
  const barRef = useRef<HTMLDivElement>(null);
  const ready = readyRows(rows);
  const pageReady = readyRows(pageRows);
  // Only the chosen awards that are still ready go: one a teammate took, or that changed, drops out.
  const chosen = ready.filter((r) => selection.ids.has(r.award.id));
  const pageChosen = pageReady.filter((r) => selection.ids.has(r.award.id));
  const everyPage = selection.mode === 'all' && chosen.length > 0;
  // Awards that became ready after the every-page choice wait to be asked for.
  const newcomers = everyPage && chosen.length < ready.length;
  const allOnPage = pageReady.length > 0 && pageChosen.length === pageReady.length;
  // The link is only worth showing when ready awards sit on other pages, or arrived since.
  const offerEveryPage = (!everyPage && allOnPage && ready.length > pageReady.length) || newcomers;
  const clear = (): void => {
    onSelect(NO_SELECTION);
    barRef.current
      ?.querySelector<HTMLInputElement>('input[type="checkbox"]:not([disabled])')
      ?.focus();
  };

  const mark = useMutation({
    mutationFn: () => completeChecks(chosen.map((r) => ({ id: r.award.id, version: r.version }))),
    onSuccess: async (result) => {
      onSelect(NO_SELECTION);
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['queue'] }),
        queryClient.invalidateQueries({ queryKey: ['home'] }),
        queryClient.invalidateQueries({ queryKey: ['check'] }),
      ]);
      toast.show(resultToast(result));
    },
    onError: async (e) => {
      await queryClient.invalidateQueries({ queryKey: ['queue'] });
      toast.show({ tone: 'problem', title: 'Nothing was marked checked', body: problemWords(e) });
    },
  });

  return (
    <div ref={barRef} className={s.markBar} role="group" aria-label="Mark awards checked">
      <TickBox
        isSelected={allOnPage}
        isIndeterminate={pageChosen.length > 0 && !allOnPage}
        isDisabled={pageReady.length === 0}
        onChange={(on) => {
          onSelect(
            on ? { mode: 'page', ids: new Set(pageReady.map((r) => r.award.id)) } : NO_SELECTION,
          );
        }}
      >
        Select all ready on this page ({formatCount(pageReady.length)})
      </TickBox>
      {/* One live region, always mounted: what the ticks reach, then what they add up to. */}
      <p className={s.markSummary} aria-live="polite">
        {everyPage && (
          <span>
            {newcomers
              ? `${formatCount(chosen.length)} ready are selected, on every page.`
              : `All ${formatCount(chosen.length)} ready are selected, on every page.`}
          </span>
        )}
        {offerEveryPage && !everyPage && (
          <span>All {formatCount(pageReady.length)} ready on this page are selected.</span>
        )}
        {(everyPage || offerEveryPage) && ' '}
        {chosen.length > 0
          ? selectionWords(chosen)
          : ready.length === 0
            ? 'None here is ready to mark checked yet: each says what it needs.'
            : 'Tick the awards you’ve looked over, or Select all ready on this page.'}
      </p>
      {(offerEveryPage || everyPage) && (
        <p className={s.markReach}>
          {/* One button whose label changes, so keyboard focus stays where it was pressed; Clear
              hands focus to the page's own box before the button goes. */}
          <Button
            variant="ghost"
            size="sm"
            onPress={() => {
              if (everyPage && !newcomers) clear();
              else onSelect({ mode: 'all', ids: new Set(ready.map((r) => r.award.id)) });
            }}
          >
            {everyPage && !newcomers
              ? 'Clear the selection'
              : `Select all ${formatCount(ready.length)} ready, on every page`}
          </Button>
          {/* With newcomers waiting, the choice made can still be cleared beside the new offer. */}
          {newcomers && (
            <Button variant="ghost" size="sm" onPress={clear}>
              Clear the selection
            </Button>
          )}
        </p>
      )}
      <Button
        variant="ghost"
        size="sm"
        aria-expanded={details.allOpen}
        onPress={() => {
          details.onAllOpen(!details.allOpen);
        }}
      >
        {details.allOpen ? 'Hide all details' : 'Show all details'}
      </Button>
      <Button
        variant="cyan"
        isDisabled={chosen.length === 0}
        isPending={mark.isPending}
        onPress={() => {
          setConfirming(true);
        }}
      >
        {chosen.length === 0 ? 'Mark checked' : `Mark ${formatCount(chosen.length)} checked`}
      </Button>
      <ConfirmDialog
        isOpen={confirming}
        onOpenChange={setConfirming}
        title={`Mark ${plural(chosen.length, 'award', 'awards')} checked?`}
        intro="You’re confirming the people, amounts, share price and charge codes each one shows in the list. Each moves to letters, with you, as if you’d marked it checked on its own page. Any that changed, or that someone took, since this list loaded stays in the queue with why."
        summary={confirmRows(chosen)}
        withoutReason
        confirmLabel={`Mark ${formatCount(chosen.length)} checked`}
        onConfirm={async () => {
          await mark.mutateAsync().catch(() => undefined);
        }}
      />
    </div>
  );
}
